import { Component, Inject, ViewEncapsulation, OnInit } from "@angular/core";
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  UntypedFormControl,
} from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { ToastrComponent } from "../util/toastr/toastr.component";
import { Misc } from "../util/misc";
import { Subject, Observable, of } from "rxjs";
import { debounceTime, map } from "rxjs/operators";
import { Contrato } from "src/app/models/contrato.model";
import { Encargo } from "src/app/models/encargo.model";
import { Profissional } from "src/app/models/profissional.model";
import { EquipeService } from "src/app/services/equipe.service";
import { ProfissionalService } from "src/app/services/profissional.service";
import { PriceParam } from "src/app/models/price-param.model";
import { EstimativaStaffPlan } from "src/app/models/estimativa-staff-plan.model";

@Component({
  selector: "app-staff-plan-projeto-form-component",
  templateUrl: "./staff-plan-projeto-form.component.html",
  encapsulation: ViewEncapsulation.None,
})
export class StaffPlanProjetoFormComponent implements OnInit {
  public keyUp = new Subject<Event>();
  filtroProfissional: UntypedFormControl = new UntypedFormControl();

  basicForm: UntypedFormGroup;
  loading = false;
  contratos: Contrato[] = [];
  encargos: Encargo[] = [];
  profissional: Profissional;
  profissionais: Profissional[];
  profissionaisFiltrada: Observable<Profissional[]>;

  idTipoContratoPadrao: number;
  idTipoEncargoPadrao: number;

  idCargoSenioridade: number;
  descricaoMetricaTipo: string;

  listaProfissionaisFiltrados: Array<Profissional> = [];
  idCargoProfissionalSelecionado = 0;

  constructor(
    private fb: UntypedFormBuilder,
    protected equipeService: EquipeService,
    protected profissionalService: ProfissionalService,
    public dialogRef: MatDialogRef<EstimativaStaffPlan>,
    private toastr: ToastrComponent,
    public misc: Misc,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.createForm();

    const subscription = this.keyUp
      .pipe(debounceTime(250))
      .subscribe((result) => {
        this.enterSave();
      });
  }

  ngOnInit() {
    this.loading = true;
    const defaultParameters: Array<PriceParam> = JSON.parse(
      localStorage.getItem("parametrosSistemaPW"),
    );

    this.idTipoContratoPadrao = defaultParameters.find(
      (p) => p.nome === "IdTipoContratoPadrao",
    ).valor;
    this.idTipoEncargoPadrao = defaultParameters.find(
      (p) => p.nome === "IdTipoEncargoPadrao",
    ).valor;

    this.profissionalService.getAll().subscribe((result) => {
      this.profissionais = result.items.filter((x) => x.ativo == true);

      var listaCargosProfissionais: Array<Profissional> = [];
      this.profissionais.forEach((element) => {
        let profissional = new Profissional();
        let cargoJaExisteNaLista = listaCargosProfissionais.find(
          (x) => x.cargoId == element.cargoId,
        );
        if (profissional && !cargoJaExisteNaLista) {
          profissional.cargo = element.cargo;
          profissional.cargoId = element.cargoId;
          listaCargosProfissionais.push(profissional);
        }
      });

      this.listaProfissionaisFiltrados = listaCargosProfissionais.filter(
        (p) => p.cargoId !== 1,
      );
      this.profissionaisFiltrada = of(listaCargosProfissionais);
      this.profissionaisFiltrada = this.filtroProfissional.valueChanges.pipe(
        map((value) => this._filter(value)),
      );

      // Se estiver em modo edição, preencher os campos após carregar profissionais
      if (this.data.isEdit && this.data.staffPlan) {
        setTimeout(() => {
          this.carregarDadosEdicao();
        }, 100);
      } else {
        this.loading = false;
      }
    });

    this.descricaoMetricaTipo = "";
  }

  private _filter(value: any): Profissional[] {
    if (typeof value === "string") {
      this.limpar();

      const filterValue = value.toLowerCase();

      return this.listaProfissionaisFiltrados.filter((option) =>
        option.cargo.nome.toLowerCase().includes(filterValue),
      );
    }
  }

  createForm() {
    this.basicForm = this.fb.group({
      id: [0],
      estimativaId: [this.data.estimativaId],
      profissionalId: [null, [Validators.required]],
      cargoId: [null, [Validators.required]],
      contratoId: [{ value: null, disabled: true }, [Validators.required]],
      encargoId: [{ value: null, disabled: true }, [Validators.required]],
      composicaoEquipe: this.fb.group({
        idCargoSenioridade: [0, [Validators.required]],
        descricaoMetricaTipo: [{ value: "", disabled: true }],
      }),
    });
  }

  limpar() {
    this.contratos = [];
    this.encargos = [];
    this.profissional = null;

    this.basicForm.patchValue({
      encargoId: null,
      contratoId: null,
      profissionalId: null,
      cargoId: null,
    });

    this.basicForm.get("composicaoEquipe")?.patchValue({
      idCargoSenioridade: 0,
      descricaoMetricaTipo: "",
    });
  }

  displayFn(profissional: Profissional): string {
    return profissional && profissional.cargo.nome
      ? profissional.cargo.nome
      : "";
  }

  close(): void {
    this.dialogRef.close();
  }

  enterSave() {
    if (!this.basicForm.valid) {
      return;
    }

    this.save();
  }

  save() {
    const formValue = this.basicForm.getRawValue();
    const profissionalIdAtual = formValue.profissionalId;
    const cargoIdAtual = formValue.cargoId;

    if (!profissionalIdAtual || !cargoIdAtual) {
      this.toastr.mostrarMensagem(
        "aviso",
        "Atenção",
        "É necessário selecionar um Cargo e Senioridade válidos.",
      );
      this.loading = false;
      return;
    }

    this.loading = true;

    // Buscar o equipeId correto da Equipe usando profissionalId e estimativaId
    this.equipeService.getByPropostaId(this.data.estimativaId).subscribe({
      next: (equipesPage: any) => {
        // Encontrar a equipe que corresponde ao profissionalId do Staff Plan
        const equipeEncontrada = equipesPage.items?.find(
          (equipe: any) => equipe.profissionalId === this.data.staffPlan.profissionalId
        );

        if (!equipeEncontrada) {
          this.toastr.mostrarMensagem(
            "aviso",
            "Atenção",
            "Não foi possível encontrar a equipe correspondente ao profissional.",
          );
          this.loading = false;
          return;
        }

        // Preparar dados para atualização usando o endpoint /api/equipe/AlterarPerfilProfissional
        // O endpoint espera: estimativaId, equipeId, profissionalId e cargoId
        const dadosAlteracao = {
          estimativaId: this.data.estimativaId,
          equipeId: equipeEncontrada.id,
          profissionalId: profissionalIdAtual,
          cargoId: cargoIdAtual,
        };

        // console.log('Enviando dados para alterar perfil:', dadosAlteracao);

        this.equipeService
          .alterarPerfilProfissional(dadosAlteracao)
          .subscribe({
            next: (response) => {
              // console.log('Resposta do servidor:', response);
              this.toastr.mostrarMensagem(
                "sucesso",
                "Sucesso",
                "Profissional atualizado com sucesso.",
              );
              this.loading = false;
              this.dialogRef.close("ok");
            },
            error: (error) => {
              // console.error('Erro ao atualizar perfil:', error);
              this.loading = false;
              this.misc.processError(
                error,
                "Não foi possível atualizar o perfil do profissional.",
              );
            },
          });
      },
      error: (error) => {
        // console.error('Erro ao buscar equipe:', error);
        this.loading = false;
        this.misc.processError(
          error,
          "Não foi possível buscar a equipe correspondente.",
        );
      },
    });
  }

  carregaContratos(idCargoSenioridade: number) {
    this.idCargoSenioridade = idCargoSenioridade;

    if (!this.idCargoProfissionalSelecionado) {
      this.basicForm
        .get("composicaoEquipe.idCargoSenioridade")
        ?.setValue(null, { emitEvent: false });
      this.toastr.mostrarMensagem(
        "aviso",
        "Atenção",
        "Selecione um Cargo antes da Senioridade.",
      );
      return;
    }

    this.limpar();

    const profissional = this.profissionais?.find(
      (x) =>
        x.cargoId === this.idCargoProfissionalSelecionado &&
        x.idCargoSenioridade === idCargoSenioridade,
    );

    if (!profissional) {
      this.toastr.mostrarMensagem(
        "aviso",
        "Atenção",
        "Não há profissional cadastrado para essa Senioridade nesse Cargo.",
      );
      this.basicForm
        .get("composicaoEquipe.idCargoSenioridade")
        ?.setValue(null, { emitEvent: false });
      return;
    }

    this.profissional = profissional;

    this.basicForm.patchValue({
      cargoId: profissional.cargo?.id ?? profissional.cargoId ?? null,
    });

    this.loading = true;

    this.contratos = [profissional.contrato].filter(Boolean);
    this.basicForm.get("contratoId")?.enable();

    if (this.idTipoContratoPadrao) {
      const contrato = this.contratos.find(
        (p) => p.id === this.idTipoContratoPadrao,
      );
      if (contrato) {
        this.basicForm.get("contratoId")?.disable();
        this.basicForm.patchValue({ contratoId: contrato.id });
      }
    }

    this.descricaoMetricaTipo = profissional.descricaoMetricaTipo || "";
    this.carregaEncargos(profissional);
  }

  carregaEncargos(profissional: Profissional) {
    if (!profissional) return;
    this.loading = true;

    this.encargos = [];
    this.basicForm.patchValue({
      encargoId: null,
      profissionalId: null,
    });

    if (profissional.encargo) {
      this.encargos = [profissional.encargo];
      this.basicForm.get("encargoId")?.enable();

      if (this.idTipoEncargoPadrao) {
        const encargo = this.encargos.find(
          (p) => p.id === this.idTipoEncargoPadrao,
        );
        if (encargo) {
          this.basicForm.get("encargoId")?.disable();
          this.basicForm.patchValue({ encargoId: encargo.id });
        }
      }
    }

    this.carregaProfissional(profissional);
  }

  carregaProfissional(profissional: Profissional) {
    if (!profissional) return;

    this.basicForm.patchValue({
      profissionalId: profissional.id ?? null,
      cargoId: profissional.cargoId ?? profissional.cargo?.id ?? null,
    });

    this.basicForm.get("composicaoEquipe")?.patchValue({
      idCargoSenioridade: profissional.idCargoSenioridade ?? 0,
      descricaoMetricaTipo: profissional.descricaoMetricaTipo ?? "",
    });

    this.idCargoSenioridade = profissional.idCargoSenioridade ?? 0;
    this.descricaoMetricaTipo = profissional.descricaoMetricaTipo ?? "";

    this.loading = false;
  }

  setCargoProfissional(prof: any) {
    const cargoId = prof?.cargoId ?? prof?.cargo?.id;
    if (!cargoId) {
      this.toastr.mostrarMensagem("aviso", "Atenção", "Cargo inválido.");
      return;
    }

    this.idCargoProfissionalSelecionado = Number(cargoId);
    this.descricaoMetricaTipo = "";

    const senioridadeAtual = this.basicForm.get(
      "composicaoEquipe.idCargoSenioridade",
    )?.value as number;
    if (senioridadeAtual) {
      this.carregaContratos(senioridadeAtual);
    }
  }

  carregarDadosEdicao() {
    const staffPlan: EstimativaStaffPlan = this.data.staffPlan;
    if (!staffPlan || !staffPlan.profissionalId) {
      this.loading = false;
      return;
    }

    this.loading = true;

    // Buscar o profissional pelo profissionalId
    const profissionalCompleto = this.profissionais.find(
      (p) => p.id === staffPlan.profissionalId
    );

    if (!profissionalCompleto) {
      this.toastr.mostrarMensagem(
        "aviso",
        "Atenção",
        "Profissional não encontrado.",
      );
      this.loading = false;
      return;
    }

    this.profissional = profissionalCompleto;

    // Preencher o campo de Cargo (autocomplete)
    const cargoProfissional = this.listaProfissionaisFiltrados.find(
      (p) => p.cargoId === profissionalCompleto.cargoId
    );
    if (cargoProfissional) {
      this.filtroProfissional.setValue(cargoProfissional);
      this.idCargoProfissionalSelecionado = profissionalCompleto.cargoId;
    }

    // Preencher Senioridade
    if (profissionalCompleto.idCargoSenioridade) {
      this.basicForm.get("composicaoEquipe.idCargoSenioridade")?.setValue(
        profissionalCompleto.idCargoSenioridade
      );
      this.idCargoSenioridade = profissionalCompleto.idCargoSenioridade;
    }

    // Preencher Tecnologia
    if (profissionalCompleto.descricaoMetricaTipo) {
      this.descricaoMetricaTipo = profissionalCompleto.descricaoMetricaTipo;
      this.basicForm.get("composicaoEquipe.descricaoMetricaTipo")?.setValue(
        profissionalCompleto.descricaoMetricaTipo
      );
    }

    // Preencher Contrato
    if (profissionalCompleto.contrato) {
      this.contratos = [profissionalCompleto.contrato];
      this.basicForm.get("contratoId")?.enable();
      this.basicForm.get("contratoId")?.setValue(profissionalCompleto.contrato.id);
      
      // Se for o contrato padrão, desabilitar
      if (this.idTipoContratoPadrao === profissionalCompleto.contrato.id) {
        this.basicForm.get("contratoId")?.disable();
      }
    }

    // Preencher Encargo
    if (profissionalCompleto.encargo) {
      this.encargos = [profissionalCompleto.encargo];
      this.basicForm.get("encargoId")?.enable();
      this.basicForm.get("encargoId")?.setValue(profissionalCompleto.encargo.id);
      
      // Se for o encargo padrão, desabilitar
      if (this.idTipoEncargoPadrao === profissionalCompleto.encargo.id) {
        this.basicForm.get("encargoId")?.disable();
      }
    }

    // Preencher outros campos do formulário
    this.basicForm.patchValue({
      id: staffPlan.id,
      profissionalId: profissionalCompleto.id,
      cargoId: profissionalCompleto.cargoId,
    });

    this.loading = false;
  }
}
